// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelChart
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS funnel visualization for a shared
 * funnel analysis result. The component presents each step's conversion bar
 * plus the overall conversion summary for onboarding and checkout flows.
 */

import type { FunnelResult } from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
} from "@geenius/ui/solidjs-css";
import { type Component, For, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS CSS funnel chart.
 *
 * @property result Funnel analysis result to visualize.
 * @property class Optional class name merged into the chart wrapper.
 */
export interface FunnelChartProps {
  result: FunnelResult;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS CSS funnel chart for a shared funnel result.
 *
 * @param props Funnel result payload for the chart.
 * @returns A funnel chart element, or an empty-state element when no steps exist.
 */
export const FunnelChart: Component<FunnelChartProps> = (props) => {
  const copy = createAnalyticsCopy();

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader>
          <CardTitle class="gn-analytics-funnel__title">
            {props.result.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Show
      when={props.result.steps.length > 0}
      fallback={
        <Card class={props.class}>
          <CardContent>
            <EmptyState title={copy.t("analytics.funnel.empty")} />
          </CardContent>
        </Card>
      }
    >
      <Card class={props.class}>
        <CardHeader>
          <CardTitle class="gn-analytics-funnel__title">
            {props.result.name}
          </CardTitle>
        </CardHeader>
        <CardContent class="gn-analytics-funnel">
          <div class="gn-analytics-funnel__steps">
            <For each={props.result.steps}>
              {(step, index) => (
                <div class="gn-analytics-funnel__step">
                  <div class="gn-analytics-funnel__step-header">
                    <span>{step.name}</span>
                    <Badge variant="secondary">{step.count}</Badge>
                  </div>
                  <Progress value={step.conversionRate} />
                  <Show when={index() > 0}>
                    <span class="gn-analytics-funnel__dropoff">
                      {copy.t("analytics.funnel.dropoff", {
                        percentage: step.dropoffRate.toFixed(1),
                      })}
                    </span>
                  </Show>
                </div>
              )}
            </For>
          </div>
          <div class="gn-analytics-funnel__summary">
            <span>
              {copy.t("analytics.funnel.overall", {
                percentage: props.result.overallConversionRate.toFixed(1),
              })}
            </span>
            <span>
              {props.result.totalConversions}/{props.result.totalEntries}
            </span>
          </div>
        </CardContent>
      </Card>
    </Show>
  );
};
